export type SoftwareProduct = {
  slug: 'aldapanucore' | 'nuspectra'
  name: string
  category: string
  summary: string
  statement: string[]
  description: string
  image: string
  imageAlt: string
  accent: string
  chapters: Array<{ number: string; title: string; copy: string; items: string[] }>
}

export const softwareProducts: Record<SoftwareProduct['slug'], SoftwareProduct> = {
  aldapanucore: {
    slug: 'aldapanucore',
    name: 'AldapaNuCore',
    category: 'Integrated engineering simulation',
    summary: 'Design, simulation, and analysis for tightly coupled physical systems.',
    statement: ['Design systems.', 'Model the physics.', 'Understand the result.'],
    description: 'AldapaNuCore is being developed as an integrated environment for engineering design, physical modeling, simulation, and technical analysis across disciplines.',
    image: '/media/software/nucore-interface.webp',
    imageAlt: 'AldapaNuCore coupled thermal simulation interface',
    accent: '#d08a45',
    chapters: [
      { number: '01', title: 'Design', copy: 'Move from system intent to a structured engineering model.', items: ['Part design', 'Sketch + geometry', 'Assembly', 'Plant layout', 'Materials'] },
      { number: '02', title: 'Simulation', copy: 'Study interacting physical behavior inside one technical workflow.', items: ['Structural', 'Thermal', 'Fluids', 'Radiation', 'Nuclear', 'Multiphysics'] },
      { number: '03', title: 'System modeling', copy: 'Represent the interfaces that make complex systems work.', items: ['Components', 'Subsystems', 'Regions', 'Interfaces', 'Connections'] },
      { number: '04', title: 'Materials', copy: 'Connect physical properties to design and simulation decisions.', items: ['Engineering properties', 'Isotopes', 'Temperature dependence', 'Radiation properties'] },
      { number: '05', title: 'Computation', copy: 'Numerical infrastructure designed around serious engineering workloads.', items: ['Solver architecture', 'NuMath', 'Numerical methods', 'Meshing', 'Convergence'] },
      { number: '06', title: 'Visualization', copy: 'Turn computed results into information engineers can inspect.', items: ['Contours', 'Radiation paths', 'Particle animation', 'Plots', 'Postprocessing'] },
    ],
  },
  nuspectra: {
    slug: 'nuspectra',
    name: 'NuSpectra',
    category: 'Radiation detection and spectroscopy',
    summary: 'Acquire, calibrate, analyze, and interpret radiation measurements.',
    statement: ['Acquire.', 'Calibrate.', 'Analyze. Interpret.'],
    description: 'NuSpectra is being developed for radiation detector acquisition, spectroscopy, calibration, measurement analysis, and interpretable scientific results.',
    image: '/media/software/nuspectra-interface.webp',
    imageAlt: 'NuSpectra gamma spectroscopy and signal-analysis interface',
    accent: '#3aa8d8',
    chapters: [
      { number: '01', title: 'Acquisition', copy: 'Bring live detector signals into a structured measurement workflow.', items: ['Live detector input', 'Count rate', 'Waveforms', 'Data logging'] },
      { number: '02', title: 'Calibration', copy: 'Relate detector response to physically meaningful energy values.', items: ['Energy calibration', 'Peak fitting', 'Reference sources', 'Calibration curves'] },
      { number: '03', title: 'Spectroscopy', copy: 'Build, refine, and inspect radiation spectra.', items: ['Spectrum generation', 'Background subtraction', 'Regions of interest', 'Peak analysis'] },
      { number: '04', title: 'Analysis', copy: 'Turn measured signals into clear scientific outputs.', items: ['Isotope identification', 'Measurement analysis', 'Statistics', 'Export'] },
      { number: '05', title: 'Detector support', copy: 'A modular foundation for measurement hardware and interfaces.', items: ['Detector types', 'Interface requirements', 'Device support', 'Acquisition configuration'] },
    ],
  },
}

export type DeveloperTool = {
  slug: 'numath' | 'aml'
  name: string
  fullName: string
  description: string
  intro: string
  capabilities: string[]
}

export const developerTools: Record<DeveloperTool['slug'], DeveloperTool> = {
  numath: {
    slug: 'numath', name: 'NuMath', fullName: 'Scientific numerical computing library',
    description: 'Numerical foundations for engineering and scientific software.',
    intro: 'NuMath is the planned numerical-computing layer behind Aldapa Systems software, designed to support repeatable scientific and engineering workflows.',
    capabilities: ['Linear algebra', 'Numerical solvers', 'ODE + PDE methods', 'Optimization', 'Convergence tools', 'Scientific data structures'],
  },
  aml: {
    slug: 'aml', name: 'AML', fullName: 'Aldapa Model Language',
    description: 'A modeling language for engineering systems, automation, and scientific computation.',
    intro: 'AML is being designed to describe physical systems, automate technical workflows, and connect model definitions to the wider Aldapa Systems software ecosystem.',
    capabilities: ['System definitions', 'Engineering models', 'Numerical computing', 'Workflow automation', 'Command-line tools', 'NuMath integration'],
  },
}
